import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Send as SendIcon,
  Psychology as AIIcon,
  ArrowBack as BackIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AIMentorService from '../services/aiMentorService';

const AIMentorPage: React.FC = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [chatMessages, setChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string}>>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    loadAIMentorConfig();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, isSending]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadAIMentorConfig = async () => {
    try {
      setConfigLoading(true);
      const config = await AIMentorService.getConfig();
      setChatMessages(config.chatHistory || []);
    } catch (error: any) {
      console.error('Failed to load AI Mentor config:', error);
      setError('Failed to load chat history');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!currentMessage.trim()) return;

    const userMessage = currentMessage.trim();
    setCurrentMessage('');

    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsSending(true);
    setError(null);

    try {
      const result = await AIMentorService.sendMessage(userMessage);

      if (result.success) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
      } else {
        setError('Failed to get response from AI Mentor');
        setChatMessages(prev => prev.slice(0, -1));
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send message. Please try again.');
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setIsSending(false);
    }
  };

  const handleClearChat = async () => {
    try {
      const result = await AIMentorService.clearChatHistory();

      if (result.success) {
        setChatMessages([]);
        setSuccess('Chat history cleared successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.message || 'Failed to clear chat history');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to clear chat history. Please try again.');
    }
  };

  if (configLoading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <CircularProgress sx={{ color: 'white' }} size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: '#f5f5f5'
    }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <BackIcon />
          </IconButton>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexGrow: 1
          }}>
            <Box sx={{
              p: 1,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex'
            }}>
              <AIIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>
                AI Study Mentor
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Your personal study assistant
              </Typography>
            </Box>
          </Box>
          <IconButton
            color="inherit"
            onClick={() => setSettingsOpen(true)}
          >
            <SettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Success Alert */}
      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ borderRadius: 0 }}
        >
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ borderRadius: 0 }}
        >
          {error}
        </Alert>
      )}

      {/* Messages Container */}
      <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        p: 2,
        pb: 10,
        '&::-webkit-scrollbar': {
          width: '6px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '3px',
          '&:hover': {
            background: '#a1a1a1'
          }
        }
      }}>
        {chatMessages.length === 0 ? (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
            color: 'text.secondary',
            px: 2
          }}>
            <Box sx={{
              p: 3,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)',
              mb: 2
            }}>
              <AIIcon sx={{ fontSize: 64, color: '#667eea' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}>
              Start a Conversation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
              Ask me anything about your studies, assignments, concepts, or any topic you need help with!
            </Typography>
          </Box>
        ) : (
          <>
            {chatMessages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  animation: 'fadeIn 0.3s ease-in',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  }
                }}
              >
                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  maxWidth: '85%',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
                }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    {msg.role === 'user' ? <PersonIcon fontSize="small" /> : <AIIcon fontSize="small" />}
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      background: msg.role === 'user'
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'white',
                      color: msg.role === 'user' ? 'white' : 'text.primary',
                      borderRadius: 3,
                      boxShadow: msg.role === 'user'
                        ? '0 4px 12px rgba(102, 126, 234, 0.3)'
                        : '0 2px 8px rgba(0,0,0,0.08)',
                      border: msg.role === 'user' ? 'none' : '1px solid #e0e0e0'
                    }}
                  >
                    {msg.role === 'assistant' ? (
                      <Box sx={{
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          marginTop: '1em',
                          marginBottom: '0.5em',
                          fontWeight: 700,
                          lineHeight: 1.3
                        },
                        '& h1': { fontSize: '1.5rem' },
                        '& h2': { fontSize: '1.25rem' },
                        '& h3': { fontSize: '1.1rem' },
                        '& p': {
                          marginTop: '0.5em',
                          marginBottom: '0.5em',
                          lineHeight: 1.6
                        },
                        '& ul, & ol': {
                          marginTop: '0.5em',
                          marginBottom: '0.5em',
                          paddingLeft: '1.5em'
                        },
                        '& li': {
                          marginTop: '0.25em',
                          marginBottom: '0.25em',
                          lineHeight: 1.5
                        },
                        '& code': {
                          background: '#f5f5f5',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '0.9em',
                          fontFamily: 'monospace',
                          color: '#d63384'
                        },
                        '& pre': {
                          background: '#f5f5f5',
                          padding: '1em',
                          borderRadius: '8px',
                          overflow: 'auto',
                          marginTop: '0.5em',
                          marginBottom: '0.5em'
                        },
                        '& pre code': {
                          background: 'transparent',
                          padding: 0,
                          color: 'inherit'
                        },
                        '& strong': {
                          fontWeight: 700,
                          color: '#1a1a1a'
                        },
                        '& em': {
                          fontStyle: 'italic'
                        },
                        '& blockquote': {
                          borderLeft: '4px solid #667eea',
                          paddingLeft: '1em',
                          marginLeft: 0,
                          color: 'text.secondary',
                          fontStyle: 'italic'
                        },
                        '& a': {
                          color: '#667eea',
                          textDecoration: 'underline'
                        },
                        '& hr': {
                          border: 'none',
                          borderTop: '1px solid #e0e0e0',
                          margin: '1em 0'
                        },
                        '& table': {
                          borderCollapse: 'collapse',
                          width: '100%',
                          marginTop: '0.5em',
                          marginBottom: '0.5em'
                        },
                        '& th, & td': {
                          border: '1px solid #e0e0e0',
                          padding: '8px',
                          textAlign: 'left'
                        },
                        '& th': {
                          background: '#f5f5f5',
                          fontWeight: 700
                        }
                      }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </Box>
                    ) : (
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {msg.content}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              </Box>
            ))}
            {isSending && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2
              }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0
                }}>
                  <AIIcon fontSize="small" />
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    background: 'white',
                    borderRadius: 3,
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <CircularProgress size={16} sx={{ color: '#667eea' }} />
                  <Typography variant="body2" color="text.secondary">
                    Thinking...
                  </Typography>
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Input Area - Fixed at bottom */}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          background: 'white',
          borderTop: '1px solid #e0e0e0',
          zIndex: 1000
        }}
      >
        <Box sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
          maxWidth: '100%',
          margin: '0 auto'
        }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type your message..."
            value={currentMessage}
            onChange={(e) => setCurrentMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={isSending}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                background: '#f8f9fa',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: '#f0f2f5',
                },
                '&.Mui-focused': {
                  background: 'white',
                  boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                  '& fieldset': {
                    borderColor: '#667eea',
                    borderWidth: 2
                  }
                },
                '& fieldset': {
                  borderColor: '#e0e0e0',
                  transition: 'all 0.3s ease'
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.95rem',
                lineHeight: 1.5
              }
            }}
          />
          <Fab
            color="primary"
            size="medium"
            onClick={handleSendMessage}
            disabled={!currentMessage.trim() || isSending}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a4291 100%)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
              },
              '&:disabled': {
                background: '#e0e0e0',
                boxShadow: 'none'
              }
            }}
          >
            <SendIcon />
          </Fab>
        </Box>
      </Paper>

      {/* Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Settings
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Chat Actions */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Chat Actions
            </Typography>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => {
                handleClearChat();
                setSettingsOpen(false);
              }}
              startIcon={<ClearIcon />}
              sx={{ textTransform: 'none' }}
            >
              Clear Chat History
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSettingsOpen(false);
              setError(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIMentorPage;
